import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { getSEOConfig } from '../config/seoConfig';
import { useLanguage } from '../context/LanguageContext';

/**
 * Privacy Policy Page
 * 
 * Legal page required for:
 * - Google Ads compliance
 * - GDPR/CCPA compliance
 * - Trust and transparency
 */
const PrivacyPolicy = () => {
  const { language } = useLanguage();
  const seoData = getSEOConfig('privacy', language);

  const content = {
    en: {
      title: "Privacy Policy",
      lastUpdated: "Last Updated: December 15, 2024",
      intro: "DSI Durango (\"we\", \"our\", or \"us\") respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you visit our website.",
      sections: [
        {
          title: "1. Information We Collect",
          content: "We collect information that you provide directly to us, including:\n• Name and contact information (email, phone number)\n• Company name and job title\n• Project details and specifications\n• Communications with us\n\nWe also automatically collect certain information when you visit our website:\n• IP address and browser type\n• Pages visited and time spent\n• Referral source"
        },
        {
          title: "2. How We Use Your Information",
          content: "We use the information we collect to:\n• Respond to your inquiries and provide quotes\n• Communicate about our services\n• Improve our website and services\n• Comply with legal obligations\n• Send marketing communications (with your consent)"
        },
        {
          title: "3. Information Sharing",
          content: "We do not sell your personal information. We may share your information with:\n• Service providers who assist in our operations\n• Professional advisors (lawyers, accountants)\n• Law enforcement when required by law"
        },
        {
          title: "4. Data Security",
          content: "We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction."
        },
        {
          title: "5. Your Rights",
          content: "You have the right to:\n• Access your personal data\n• Correct inaccurate data\n• Request deletion of your data\n• Opt-out of marketing communications\n• Lodge a complaint with a supervisory authority"
        },
        {
          title: "6. Cookies and Tracking",
          content: "We use cookies and similar tracking technologies to improve your experience. You can control cookies through your browser settings."
        },
        {
          title: "7. Third-Party Services",
          content: "Our website may use third-party services:\n• PostHog for analytics\n• Bitrix24 for customer relationship management\n• WhatsApp for communication\n\nThese services have their own privacy policies."
        },
        {
          title: "8. International Data Transfers",
          content: "Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place."
        },
        {
          title: "9. Changes to This Policy",
          content: "We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page."
        },
        {
          title: "10. Contact Us",
          content: "If you have questions about this privacy policy, please contact us at:\n\nEmail: ventas@dsidurango.com\nPhone: +52-618-171-9959"
        }
      ]
    },
    es: {
      title: "Política de Privacidad",
      lastUpdated: "Última actualización: 15 de diciembre de 2024",
      intro: "DSI Durango (\"nosotros\" o \"nuestro\") respeta su privacidad y se compromete a proteger sus datos personales. Esta política de privacidad explica cómo recopilamos, usamos y protegemos su información cuando visita nuestro sitio web.",
      sections: [
        {
          title: "1. Información que Recopilamos",
          content: "Recopilamos información que usted nos proporciona directamente, incluyendo:\n• Nombre e información de contacto (correo electrónico, teléfono)\n• Nombre de empresa y cargo\n• Detalles y especificaciones del proyecto\n• Comunicaciones con nosotros\n\nTambién recopilamos automáticamente cierta información cuando visita nuestro sitio web:\n• Dirección IP y tipo de navegador\n• Páginas visitadas y tiempo transcurrido\n• Fuente de referencia"
        },
        {
          title: "2. Cómo Usamos su Información",
          content: "Utilizamos la información que recopilamos para:\n• Responder a sus consultas y proporcionar cotizaciones\n• Comunicarnos sobre nuestros servicios\n• Mejorar nuestro sitio web y servicios\n• Cumplir con obligaciones legales\n• Enviar comunicaciones de marketing (con su consentimiento)"
        },
        {
          title: "3. Compartir Información",
          content: "No vendemos su información personal. Podemos compartir su información con:\n• Proveedores de servicios que ayudan en nuestras operaciones\n• Asesores profesionales (abogados, contadores)\n• Autoridades cuando lo requiera la ley"
        },
        {
          title: "4. Seguridad de Datos",
          content: "Implementamos medidas técnicas y organizativas apropiadas para proteger sus datos personales contra acceso no autorizado, alteración, divulgación o destrucción."
        },
        {
          title: "5. Sus Derechos",
          content: "Usted tiene derecho a:\n• Acceder a sus datos personales\n• Corregir datos inexactos\n• Solicitar la eliminación de sus datos\n• Optar por no recibir comunicaciones de marketing\n• Presentar una queja ante una autoridad supervisora"
        },
        {
          title: "6. Cookies y Rastreo",
          content: "Utilizamos cookies y tecnologías de rastreo similares para mejorar su experiencia. Puede controlar las cookies a través de la configuración de su navegador."
        },
        {
          title: "7. Servicios de Terceros",
          content: "Nuestro sitio web puede usar servicios de terceros:\n• PostHog para análisis\n• Bitrix24 para gestión de relaciones con clientes\n• WhatsApp para comunicación\n\nEstos servicios tienen sus propias políticas de privacidad."
        },
        {
          title: "8. Transferencias Internacionales de Datos",
          content: "Su información puede ser transferida y procesada en países distintos al suyo. Aseguramos que existan salvaguardas apropiadas."
        },
        {
          title: "9. Cambios a Esta Política",
          content: "Podemos actualizar esta política de privacidad de vez en cuando. Le notificaremos de cualquier cambio publicando la nueva política en esta página."
        },
        {
          title: "10. Contáctenos",
          content: "Si tiene preguntas sobre esta política de privacidad, contáctenos en:\n\nEmail: ventas@dsidurango.com\nTeléfono: +52-618-171-9959"
        }
      ]
    }
  };

  const t = content[language];

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        ogImage={seoData.ogImage}
      />
      
      <Header />
      
      <main className="min-h-screen bg-black pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.title}
          </h1>
          <p className="text-gray-400 mb-8">{t.lastUpdated}</p>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              {t.intro}
            </p>

            {t.sections.map((section, index) => (
              <div key={index} className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {section.title}
                </h2>
                <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                  {section.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      </main>
      
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
